<template>
  <applicants-export
    :title="title"
    :statuses="realStatuses"
    :value="initialValue"
    :loading="isLoading"
    @submit="handleSubmit"
    @cancel="handleCancel"
  >
    <template
      v-if="isReportEmpty"
      #error
    >
      <span :class="$style.error">{{ emptyReportErrorMessage }}</span>
    </template>
  </applicants-export>
</template>

<script setup lang="ts">
import type { AxiosError } from 'axios';
import { computed, defineProps, defineEmits, ref } from 'vue';
import { useStore } from 'vuex';
import type { Pipeline } from '@/shared/legacy/types/pipeline';
import type { Status } from '@/shared/legacy/types/status';
import { VacancyAPI } from '@/shared/legacy/api/vacancy';
import { useI18N } from '@/shared/legacy/lib/use-i18n';
import ApplicantsExport from '../../applicants-export/applicants-export.vue';
import { ExportType } from './export-type';
import { isEmptyReportError } from './helpers';

const { accountVacancyStatusGroup, applicantId, vacancyId, type } = defineProps<IProps>();

const emit = defineEmits(['submit', 'cancel']);

const i18n = useI18N();

type ValueOf<T> = T[keyof T];

type IProps = {
  type: ValueOf<typeof ExportType>;
  applicantId?: number;
  vacancyId: number;
  accountVacancyStatusGroup?: number;
};

const isLoading = ref(false);
const store = useStore();
const pipelineById = computed(() => store.getters['pipelines/pipelineById']);
const statuses = computed(() => store.getters['vacancyStats/statuses']);
const pipelineStatusIds = computed(() =>
  accountVacancyStatusGroup
    ? getPipelineStatusIds(pipelineById.value(accountVacancyStatusGroup))
    : null
);

const realStatuses = computed(() => {
  if (applicantId) {
    return [];
  }

  return filterStatuses(
    statuses.value.filter(({ virtual }) => !virtual),
    pipelineStatusIds.value
  );
});

const title = computed(() => {
  if (type === ExportType.LIST) {
    return i18n('title.list');
  } else if (type === ExportType.SURVEY_TYPE_A) {
    return i18n('title.survey_type_a');
  } else if (type === ExportType.SURVEY_TYPE_Q) {
    return i18n('title.survey_type_q');
  }
  return null;
});

const initialValue = {
  field: [],
  status: realStatuses.value.map(({ id }) => id)
};

const isReportEmpty = ref(false);
const emptyReportErrorMessage = computed(() => {
  if (!isReportEmpty.value) {
    return undefined;
  }
  if (type === 'survey_type_a') {
    return i18n('error.empty.survey_type_a');
  } else if (type === 'survey_type_q') {
    return i18n('error.empty.survey_type_q');
  }
  return undefined;
});

const handleSubmit = (values: {
  field: Array<string>;
  status: Array<number>;
}) => {
  isLoading.value = true;

  if (type === ExportType.LIST) {
    return VacancyAPI.fetchStopList(vacancyId, values)
      .then(() => {
        emit('submit');
      })
      .finally(() => {
        isLoading.value = false;
      });
  }

  let payload;
  if (applicantId) {
    payload = {
      vacancy_id: vacancyId,
      field: values.field,
      applicant_id: applicantId
    };
  } else {
    payload = {
      vacancy_id: vacancyId,
      field: values.field,
      status: values.status
    };
  }

  const handleError = (err: AxiosError) => {
    if (isEmptyReportError(err)) {
      isReportEmpty.value = true;
    } else {
      isReportEmpty.value = false;
      throw err;
    }
  };

  if (type === ExportType.SURVEY_TYPE_A) {
    return VacancyAPI.generateSurveyTypeAReport(payload)
      .then(() => {
        isReportEmpty.value = false;
        emit('submit');
      })
      .catch(handleError)
      .finally(() => {
        isLoading.value = false;
      });
  }

  if (type === ExportType.SURVEY_TYPE_Q) {
    return VacancyAPI.generateSurveyTypeQReport(payload)
      .then(() => {
        isReportEmpty.value = false;
        emit('submit');
      })
      .catch(handleError)
      .finally(() => {
        isLoading.value = false;
      });
  }
};

const handleCancel = () => emit('cancel');

function getPipelineStatusIds(pipeline: Pipeline) {
  return pipeline?.statuses.map((status) => status.account_vacancy_status);
}

function filterStatuses(statuses: Array<Status>, pipelineStatusIds: Array<number> | null) {
  return pipelineStatusIds ? statuses.filter((s) => pipelineStatusIds.includes(s.id)) : statuses;
}
</script>

<i18n lang="json">
{
  "title.list": {
    "ru_RU": "Лист кандидатов",
    "en_US": "Applicants list"
  },
  "title.survey_type_a": {
    "ru_RU": "Обратная связь по кандидатам на вакансии",
    "en_US": "Feedback on candidates for vacancy"
  },
  "title.survey_type_q": {
    "ru_RU": "Анкеты кандидатов на вакансии",
    "en_US": "Candidate questionnaires for vacancy"
  },
  "error.empty.survey_type_a": {
    "ru_RU": "На выбранных этапах нет кандидатов с формами обратной связи",
    "en_US": "No applicants with feedback at the selected statuses"
  },
  "error.empty.survey_type_q": {
    "ru_RU": "На выбранных этапах нет кандидатов с анкетами",
    "en_US": "No applicants with questionnaires at the selected statuses"
  }
}
</i18n>

<style module>
.error {
  composes: form-group__error from global;
  display: block;
}
</style>
